import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import { AiSparkleDefaultIcon } from '../Icons/AiSparkleDefaultIcon';
import { Box, BoxProps, IconButton, Typography, useTheme } from '@mui/material';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import type { Theme } from '@mui/material/styles';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  atlasFontTextBody,
  atlasInverseGhostHoverSx,
  atlasRadiusLg,
  atlasSpacing,
  atlasToastAlertContainerSx,
  atlasToastAlertIconStripSx,
  atlasToastBodySx,
  type AtlasLensTheme,
} from '../Form/atlasLensStyles';
import { resolveAtlasLensColors } from '../Form/formFieldSpec';
import { ATLAS_LENS_SEMANTIC_VARS } from '../Form/atlasLensTokens';

export type AtlasTranslationStartedToastProps = {
  message: string;
  onDismiss: () => void;
} & Omit<BoxProps, 'children'>;

/** Figma translation-started toast — dark surface, cyan accent strip, sparkle icon, message, dismiss control. */
export const AtlasTranslationStartedToast = forwardRef<
  HTMLDivElement,
  AtlasTranslationStartedToastProps
>(function AtlasTranslationStartedToast({ message, onDismiss, ...rest }, ref) {
  const { t } = useTranslation();
  const theme = useTheme() as Theme & LensTheme;
  const atlasTheme = theme as AtlasLensTheme;
  const { tokens } = theme;
  const { toastSurface, coreBlue80, typeInverse } = resolveAtlasLensColors(theme);
  const borderColor =
    tokens?.semantic?.color?.ui?.divider?.default?.value ?? ATLAS_LENS_SEMANTIC_VARS.dividerDefault;
  const dismissButtonSize = tokens?.core?.spacing?.['5']?.value ?? '40px';
  const closeIconSize = atlasSpacing(atlasTheme, '3');

  return (
    <Box
      ref={ref}
      {...rest}
      role="status"
      aria-live="polite"
      data-qa-anchor="atlas-toast-translation-started"
      sx={{
        ...atlasToastAlertContainerSx(atlasTheme),
        background: toastSurface,
        border: `1px solid ${borderColor}`,
      }}
    >
      <Box
        sx={{
          ...atlasToastAlertIconStripSx(atlasTheme),
          background: coreBlue80,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: atlasSpacing(atlasTheme, '3'),
            height: atlasSpacing(atlasTheme, '3'),
            '& > dil-ai-sparkle-default-icon': {
              display: 'block',
              width: '100%',
              height: '100%',
            },
          }}
        >
          <AiSparkleDefaultIcon size="md" iconColor={toastSurface} />
        </Box>
      </Box>
      <Box sx={atlasToastBodySx(atlasTheme)}>
        <Typography
          sx={{
            ...atlasFontTextBody(atlasTheme),
            color: typeInverse,
            flex: 1,
          }}
        >
          {message}
        </Typography>
        <IconButton
          onClick={onDismiss}
          aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
          sx={{
            flexShrink: 0,
            width: dismissButtonSize,
            height: dismissButtonSize,
            p: atlasSpacing(atlasTheme, '1'),
            borderRadius: atlasRadiusLg(atlasTheme),
            color: typeInverse,
            ...atlasInverseGhostHoverSx(atlasTheme),
          }}
        >
          <CloseIcon style={{ fontSize: closeIconSize }} />
        </IconButton>
      </Box>
    </Box>
  );
});
